import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '@app/hooks/stores';
import { Table } from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import updateLocale from 'dayjs/plugin/updateLocale';
import RoomModal from '@app/common/RoomModal';
import { PAGE_SIZE_OPTIONS, ROOM_MODAL_TAB_KEYS } from '@app/constants';
import AlarmsModal from '@app/common/AlarmsModal';

dayjs.extend(duration);
dayjs.extend(updateLocale);

interface RoomsStatusProps {}

const ReportStatus: React.FC<RoomsStatusProps> = () => {
  const { reportStore } = useStores();

  const { getRoomDataById, roomData, actionLoading, getRoomAlarmsById, getRoomMeasurementsById, report, dubaiTZ } =
    reportStore;

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [roomDataModalVisible, setRoomDataModalVisible] = useState<boolean>(false);
  const [alarmsModalVisible, setAlarmsModalVisible] = useState<boolean>(false);
  const [selectedRoomData, setSelectedRoomData] = useState<Record<string, string>>({});

  const fetchRoomDataById = (id: string) => {
    getRoomMeasurementsById(id);
  };

  const handleRoomDataModalOpen = (roomId: string) => () => {
    if (!roomDataModalVisible) fetchRoomDataById(roomId);
    const roomReport = report?.roomsData.find(building => building.roomId === Number(roomId));
    if (roomReport) {
      setSelectedRoomData({
        roomName: roomReport.roomName,
        buildingName: roomReport.buildingName,
        levelName: roomReport.levelName
      });
    }

    handleRoomModal();
  };

  const handleAlarmModalOpen = (roomId: string, roomName: string) => () => {
    getRoomAlarmsById(roomId);
    if (!selectedRoomData.roomName) {
      const roomReport = report?.roomsData.find(building => building.roomId === Number(roomId));
      if (roomReport) {
        setSelectedRoomData({
          roomName: roomReport.roomName,
          buildingName: roomReport.buildingName,
          levelName: roomReport.levelName
        });
      }
    }

    handleAlarmsModal();
  };

  const handleRoomModal = () => {
    setRoomDataModalVisible(!roomDataModalVisible);
  };

  const handleAlarmsModal = () => {
    setAlarmsModalVisible(!alarmsModalVisible);
  };

  const handleModalTabChange = (key: string) => {
    if (key === ROOM_MODAL_TAB_KEYS.Alarm && !roomData?.alarms) {
      getRoomAlarmsById();
    }

    if (key === ROOM_MODAL_TAB_KEYS.Data && !roomData?.roomInfo) {
      getRoomDataById();
    }
  };

  const tableColumns = [
    {
      title: 'Room',
      width: 120,
      align: 'center' as const,
      render: value => {
        return (
          <div className="actionColumn" onClick={handleRoomDataModalOpen(value.roomId)}>
            {value.roomName}
          </div>
        );
      }
    },
    {
      title: 'Average Temperature(°C)',
      dataIndex: 'temp1',
      width: 30,
      align: 'center' as const,
      sorter: (a, b) => a.temp1 - b.temp1,
      render: temp1 => <span>{temp1?.toFixed(2)}</span>
    },
    {
      title: 'Fan running time',
      dataIndex: 'fanTimeFormatted',
      width: 30,
      align: 'center' as const,
      sorter: (a, b) => a.fanTime - b.fanTime,
      render: fanTimeFormatted => <span>{fanTimeFormatted}</span>
    },
    {
      title: 'Energy',
      dataIndex: 'energy',
      width: 40,
      align: 'center' as const,
      sorter: (a, b) => a.energy - b.energy,
      render: energy => <span>{energy.toFixed(2)} kWh</span>
    },
    {
      title: 'Alarms',

      width: 40,
      align: 'center' as const,
      sorter: (a, b) => a.alarmsCount - b.alarmsCount,
      render: value => (
        <div className="actionColumn" onClick={handleAlarmModalOpen(value.roomId, value.roomName)}>
          {value.alarmsCount} alarms
        </div>
      )
    },
    {
      title: 'Connection time',
      dataIndex: 'conTimeFormatted',
      width: 60,
      align: 'center' as const,
      sorter: (a, b) => a.conTime - b.conTime,
      render: conTimeFormatted => <span>{conTimeFormatted}</span>
    }
  ];
  const fullRoomData = { ...roomData, extraData: selectedRoomData };

  return (
    <>
      <AlarmsModal
        isModalVisible={alarmsModalVisible}
        data={fullRoomData}
        handleOk={handleAlarmsModal}
        loading={actionLoading}
      />

      <RoomModal
        isModalVisible={roomDataModalVisible}
        handleCancel={handleRoomModal}
        data={fullRoomData}
        timeZoneOffset={dubaiTZ}
        handleOk={handleRoomModal}
        loading={actionLoading}
        onTabChange={handleModalTabChange}
      />

      <Table
        dataSource={report?.roomsData}
        columns={tableColumns}
        rowKey={record => record.roomId}
        pagination={{
          position: ['bottomRight'],
          size: 'default',
          current: page,
          pageSize,

          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: total => {
            return total === 1 ? `Total ${total} item` : `Total ${total} items`;
          },
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          }
        }}
        // scroll={{ y: 300 }}
        size="small"
        loading={actionLoading}
      />
    </>
  );
};

export default observer(ReportStatus);
